import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Loader2, Send, RefreshCw, CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Ask a squad's parents on WhatsApp whether their child is coming to a
 *  training date. Replies (YES/NO) land back in the portal automatically. */
export const AvailabilityPoll = ({ teams = [] }) => {
  const [teamId, setTeamId] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [location, setLocation] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [polls, setPolls] = useState(null);

  const body = () => ({ team_id: teamId, date, start_time: startTime, location });

  const doPreview = async () => {
    if (!teamId || !date) { toast.error("Pick a team and a date"); return; }
    try {
      const r = await axios.post(`${API}/admin/whatsapp/availability-poll`,
        { ...body(), dry_run: true }, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the list");
    }
  };

  const send = async () => {
    if (!teamId || !date) { toast.error("Pick a team and a date"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/availability-poll`, body(),
        { withCredentials: true });
      if (r.data.error === "whatsapp_disabled") {
        toast.error("Turn WhatsApp on in Credentials first");
      } else {
        toast.success(`Asked ${r.data.sent} parent(s) · ${r.data.skipped} opted out`);
        loadReplies();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the poll");
    } finally {
      setSending(false);
    }
  };

  const loadReplies = async () => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/availability-polls`, {
        params: date ? { date } : {}, withCredentials: true,
      });
      setPolls(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load replies");
    }
  };

  return (
    <Card className="p-6 bg-slate-900/50" data-testid="availability-poll-card">
      <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
        <CalendarCheck className="w-5 h-5 text-emerald-400" /> Ask who&apos;s coming
      </h3>
      <p className="text-sm text-slate-400 mb-5">
        Replaces the group poll. Parents reply YES or NO in WhatsApp and the answer is saved
        against that child and date — the same answers the coach&apos;s attendance sheet and the
        rain-out credit picker use.
      </p>
      <p className="text-xs text-slate-500 mb-5">
        For replies to come back, point your Twilio number&apos;s &ldquo;when a message comes in&rdquo;
        webhook at <code className="bg-slate-800 px-1 rounded">{`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`}</code>
      </p>

      <div className="grid gap-3 sm:grid-cols-2 max-w-2xl">
        <div>
          <Label className="text-white text-sm">Team</Label>
          <select value={teamId} onChange={(e) => { setTeamId(e.target.value); setPreview(null); }}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 text-white rounded-md text-sm"
            data-testid="availability-team-select">
            <option value="">Select team...</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name} {t.age_group ? `(${t.age_group})` : ""}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-white text-sm">Training date</Label>
          <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }}
            className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="availability-date" />
        </div>
        <div>
          <Label className="text-white text-sm">Kick-off (optional)</Label>
          <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
            className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="availability-time" />
        </div>
        <div>
          <Label className="text-white text-sm">Venue (optional)</Label>
          <Input value={location} onChange={(e) => setLocation(e.target.value)}
            placeholder="Padang" className="mt-1 bg-slate-800 border-slate-700 text-white"
            data-testid="availability-location" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button variant="outline" onClick={doPreview}
          className="border-slate-600 text-slate-200 hover:bg-slate-800"
          data-testid="availability-preview-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Preview recipients
        </Button>
        <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="availability-send-btn">
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Ask on WhatsApp
        </Button>
        <Button variant="ghost" onClick={loadReplies} className="text-slate-300"
          data-testid="availability-replies-btn">
          See replies
        </Button>
      </div>

      {preview && (
        <Card className="p-4 mt-4 bg-slate-800/50 border-slate-700" data-testid="availability-preview">
          <p className="text-sm text-white font-semibold">
            {preview.recipient_count} parent number{preview.recipient_count === 1 ? "" : "s"} would be asked
          </p>
          <ul className="mt-2 space-y-1 text-xs font-mono text-slate-300">
            {(preview.sample || []).map((s, i) => (
              <li key={i}>{s.phone} <span className="text-slate-500">({s.player_name})</span></li>
            ))}
          </ul>
        </Card>
      )}

      {polls && (
        <Card className="p-4 mt-4 bg-slate-800/50 border-slate-700" data-testid="availability-replies">
          <p className="text-sm text-white font-semibold mb-2">
            {polls.replied} of {polls.asked} replied
          </p>
          {polls.asked === 0 && <p className="text-xs text-slate-500">Nobody asked for that date yet.</p>}
          <div className="space-y-1">
            {(polls.polls || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs text-slate-300"
                data-testid={`availability-reply-${p.player_id}`}>
                <span className="truncate">{p.player_name} <span className="text-slate-500">· {p.date}</span></span>
                {p.attending === true && (
                  <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">coming</Badge>
                )}
                {p.attending === false && (
                  <Badge className="bg-amber-500/20 text-amber-300 text-[10px]">not coming</Badge>
                )}
                {p.attending === null && (
                  <Badge className="bg-slate-600/30 text-slate-400 text-[10px]">waiting</Badge>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </Card>
  );
};
